import React, { useMemo, useState } from 'react';
import i18n from '../../../../../i18n';
import { fmt, formatMoney } from '../../../data/metrics';
import type { DrillPanel } from '../../../types';
import { DrillTile } from './DrillTile';
import { DrillBarChart } from './charts/DrillBarChart';
import { ellipsize } from './outletNames';

type PosSortKey = 'revenue' | 'qty' | 'avgPrice' | 'name';
type PosLimit = 10 | 25 | 50 | 100;
const POS_LIMITS: PosLimit[] = [10, 25, 50, 100];

export function DrillPosItemsSection({
  panel,
}: {
  metricKey: string;
  panel: Extract<DrillPanel, { kind: 'posItems' }>;
}) {
  const [query, setQuery] = useState('');
  const [sortKey, setSortKey] = useState<PosSortKey>('revenue');
  const [sortDir, setSortDir] = useState<'desc' | 'asc'>('desc');
  const [limit, setLimit] = useState<PosLimit>(10);

  const top5 = useMemo(
    () => [...panel.items].sort((a, b) => b.revenue - a.revenue).slice(0, 5),
    [panel.items],
  );

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    let rows = panel.items;
    if (q) rows = rows.filter(it => it.name.toLowerCase().includes(q));
    const dir = sortDir === 'asc' ? 1 : -1;
    return [...rows].sort((a, b) => {
      if (sortKey === 'name') return a.name.localeCompare(b.name) * dir;
      return (a[sortKey] - b[sortKey]) * dir;
    });
  }, [panel.items, query, sortKey, sortDir]);

  const visible = filtered.slice(0, limit);
  const unit = panel.unit || visible[0]?.currency || '€';
  const barMax = Math.max(...visible.map(r => r.revenue), 0.0001);
  const total = Math.max(panel.totalRevenue, 0.0001);

  const inspector = (
    <>
      <div className="pos-items-toolbar" onClick={e => e.stopPropagation()}>
        <input
          type="search"
          className="pos-items-search"
          placeholder={i18n.t('performance:drillUi.searchItems')}
          value={query}
          onChange={e => setQuery(e.target.value)}
          aria-label={i18n.t('performance:drillUi.searchPosItems')}
        />
        <label className="pos-items-ctrl">
          <span>{i18n.t('performance:drillUi.sort')}</span>
          <select
            value={sortKey}
            onChange={e => {
              const next = e.target.value as PosSortKey;
              setSortKey(next);
              setSortDir(next === 'name' ? 'asc' : 'desc');
            }}
          >
            <option value="revenue">{i18n.t('performance:drillUi.sortRevenue')}</option>
            <option value="qty">{i18n.t('performance:drillUi.sortQty')}</option>
            <option value="avgPrice">{i18n.t('performance:drillUi.sortAvgPrice')}</option>
            <option value="name">{i18n.t('performance:drillUi.sortName')}</option>
          </select>
        </label>
        <button
          type="button"
          className="pos-items-dir"
          onClick={() => setSortDir(d => (d === 'asc' ? 'desc' : 'asc'))}
          aria-label={sortDir === 'asc' ? i18n.t('performance:drillUi.ascending') : i18n.t('performance:drillUi.descending')}
        >
          {sortDir === 'asc' ? '↑' : '↓'}
        </button>
        <label className="pos-items-ctrl">
          <span>{i18n.t('performance:drillUi.show')}</span>
          <select
            value={limit}
            onChange={e => setLimit(Number(e.target.value) as PosLimit)}
          >
            {POS_LIMITS.map(n => (
              <option key={n} value={n}>
                {n === 100 ? i18n.t('performance:drillUi.all') : n}
              </option>
            ))}
          </select>
        </label>
      </div>
      {visible.length === 0 ? (
        <div className="si-empty">{i18n.t('performance:drillUi.noItemsMatch')}</div>
      ) : (
        <div className="dcrux-si-list" style={{ marginTop: 10 }}>
          {visible.map(r => (
            <div className="dcrux-si-row" key={r.id}>
              <span className="dcrux-si-name" title={r.name}>
                {r.name}
              </span>
              <span className="dcrux-si-track">
                <span
                  className="dcrux-si-fill"
                  style={{ width: `${(r.revenue / barMax) * 100}%` }}
                />
              </span>
              <span className="dcrux-si-val">
                {formatMoney(r.revenue, r.currency || unit)}
                <span style={{ color: 'var(--ink-3)', fontWeight: 500 }}>
                  {' '}
                  · {((r.revenue / total) * 100).toFixed(0)}%
                </span>
                {r.compareRevenue != null ? (
                  <span
                    className="dcrux-cmp-chip"
                    title={i18n.t('performance:drillUi.vsComparePeriod')}
                  >
                    {formatMoney(r.compareRevenue, r.currency || unit)}
                    {(() => {
                      const delta =
                        r.compareRevenue !== 0
                          ? +(
                              ((r.revenue - r.compareRevenue) /
                                r.compareRevenue) *
                                100
                            ).toFixed(1)
                          : null;
                      return delta != null
                        ? ` · ${delta >= 0 ? '+' : ''}${delta}%`
                        : '';
                    })()}
                  </span>
                ) : null}
              </span>
            </div>
          ))}
        </div>
      )}
    </>
  );

  return (
    <DrillTile
      sectionId="pos-items"
      span={4}
      title={panel.title}
      meta={i18n.t('performance:drillUi.itemsCount', { count: panel.items.length })}
      n={i18n.t('performance:drillUi.nItemsSold', {
        n: fmt.num(panel.items.reduce((a, it) => a + (it.qty || 0), 0)),
      })}
      inspector={inspector}
    >
      <DrillBarChart
        data={top5.map(r => ({
          // Articles, not venues: no prefix stripping ("Pizza …" is meaningful).
          name: ellipsize(r.name),
          fullName: r.name,
          value: r.revenue,
        }))}
        fmtVal={v => formatMoney(v, unit)}
        height={140}
        horizontal
      />
    </DrillTile>
  );
}
